import gettext, { ngettext } from '@sdk/lang/gettext';
import useAuth from '@sdk/authentication/useAuth';
import { useIntegrations } from '@sdk/integrations/IntegrationsContext';
import SlingTip from '@sdk/common/slingTip/SlingTip';
import Text14 from '@sdk/components/ui/typography/Text14';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import { SUBSCRIPTION_NAMES } from '@sdk/account/constants';
import dateToMonthDay from '@sdk/utils/dateFormat/dateToMonthDay';
import getFourDigitYear from '@sdk/utils/dateFormat/getFourDigitYear';

import { useBillingInfo } from '@web/account/billing/BillingInfoContext';

type Props = {
  showPricing: boolean;
};

const BillingPlansBanner = ({ showPricing }: Props) => {
  const { currentUser } = useAuth();
  const { billingInfo } = useBillingInfo();
  const { toast } = useIntegrations();

  if (!toast.enabled && currentUser.org.toastSubscription === 'pro') {
    return (
      <SlingTip>
        <Text14 semiBold themeColor={ThemeColorType.white}>
          {gettext('You are using the Free plan of Sling.')}
          <br />
          {gettext(
            'Reconnect to Toast or upgrade your subscription to gain access to more features.'
          )}
        </Text14>
      </SlingTip>
    );
  }

  if (
    !billingInfo.isPremium &&
    !billingInfo.subscriptionCancelDate &&
    billingInfo.premiumTrialStartDate
  ) {
    return null;
  }
  const { planName, trialDaysLeft } = billingInfo;

  if (billingInfo.isOnTrial && !showPricing) {
    return (
      <SlingTip>
        <Text14 semiBold themeColor={ThemeColorType.white}>
          {trialDaysLeft
            ? // TODO this is faulty logic but was with the former implementation as well;
              // trialDaysLeft will always be > 0 if isOnTrial is true, so it will never get
              // to the `else` part of the condition
              ngettext(
                'Your %(planName)s trial will expire in %(trialDaysLeft)s day, at which point paid features will be inaccessible.',
                'Your %(planName)s trial will expire in %(trialDaysLeft)s days, at which point paid features will be inaccessible.',
                trialDaysLeft,
                { planName, trialDaysLeft }
              )
            : gettext(
                'Your %(planName)s trial will expire in less than one day, at which point paid features will be inaccessible.',
                { planName }
              )}
        </Text14>
      </SlingTip>
    );
  }

  if (billingInfo.isPremium && billingInfo.scheduledToResumeDate) {
    return (
      <SlingTip>
        <Text14 semiBold themeColor={ThemeColorType.white}>
          {gettext('Your %(subscriptionName)s subscription has been paused.', {
            subscriptionName: SUBSCRIPTION_NAMES[billingInfo.pausedSubscriptionLevel],
          })}
          <br />
          {gettext(
            'You will be able to access the paid features until the end of the current billing period.'
          )}
          <br />
          {gettext(
            'You will automatically regain access to paid features on %(monthDay)s, %(year)s',
            {
              monthDay: dateToMonthDay(billingInfo.scheduledToResumeDate),
              year: getFourDigitYear(billingInfo.scheduledToResumeDate),
            }
          )}
        </Text14>
      </SlingTip>
    );
  }

  if (billingInfo.isPremium && billingInfo.subscriptionCancelDate) {
    return (
      <SlingTip>
        <Text14 semiBold themeColor={ThemeColorType.white}>
          {gettext(
            'Your %(planName)s subscription has been canceled. You will be able to access the paid features until the end of the current billing period.',
            { planName }
          )}
        </Text14>
      </SlingTip>
    );
  }

  if (billingInfo.scheduledToResumeDate) {
    return (
      <SlingTip>
        <Text14 semiBold themeColor={ThemeColorType.white}>
          {gettext(
            'Your %(planName)s subscription has been paused. You will automatically regain access to paid features on  %(monthDay)s, %(year)s',
            {
              planName,
              monthDay: dateToMonthDay(billingInfo.scheduledToResumeDate),
              year: getFourDigitYear(billingInfo.scheduledToResumeDate),
            }
          )}
        </Text14>
      </SlingTip>
    );
  }

  if (billingInfo.paymentFailedDate) {
    return (
      <SlingTip>
        <Text14 semiBold themeColor={ThemeColorType.white}>
          {gettext('Your last payment has failed.')}
          <br />
          {gettext('Your subscription has been cancelled.')}
        </Text14>
      </SlingTip>
    );
  }

  return null;
};

export default BillingPlansBanner;
